/**
 * Logging — the single entry point.
 *
 * Every consumer in `src/` imports from `@logging` and nothing deeper. That
 * is the rule that keeps this from fragmenting again: the previous shape was
 * four modules (`utils/logger`, `utils/analytics`, `utils/error-reporting`,
 * `schemas/analytics-events`) with overlapping responsibilities and no
 * obvious front door, and it drifted until nobody could say which events
 * were still flowing.
 *
 * ## What a caller needs
 *
 * ```ts
 * import { createLogger, logError, trackCtaClick } from '@logging';
 *
 * const log = createLogger('ContactUsPage');
 * log.info('mounted');                       // console, dev only
 * log.error('submit failed', err);           // console + dashboards + Error Reporting
 *
 * trackCtaClick('landing_hero_waitlist');    // a product event
 * ```
 *
 * ## Where things live
 *
 * - `events/catalog.ts` — the published event contract. Add events here.
 * - `transports/index.ts` — where events go. Add destinations here.
 * - `config.ts` — every knob, resolved from the environment once.
 * - `README.md` — the contract as prose, for the dashboards owner.
 *
 * Nothing else needs to be read to use this module, and nothing outside
 * these four files needs to change to extend it.
 *
 * @module logging
 */

import { initErrorReporting } from './error-reporting';
import { initErrorInstrumentation } from './instrumentation/errors';
import { initSessionInstrumentation } from './instrumentation/session';
import { diagnostic } from './core/diagnostic';
import { telemetry } from './telemetry';

// =============================================================================
// PUBLIC SURFACE
// =============================================================================

export { createLogger, logger } from './console-logger';
export type { Logger } from './console-logger';

export { logError } from './telemetry';
export type { LogErrorOptions } from './telemetry';

export { trackPageView } from './instrumentation/page-views';
export {
  trackCtaClick,
  trackFormSubmitted,
  trackDeckGateAttempt,
} from './instrumentation/interactions';

export { reportError } from './error-reporting';

export type { ErrorCategory, Outcome, Severity, WireEvent } from './core/types';
export type { WebEventName } from './events/catalog';
export { EVENT_CATALOG, EVENT_NAMES } from './events/catalog';

/** Escape hatch for emitting a catalog event no helper wraps yet. Prefer a
 *  named helper — it is the thing a call site can be reviewed against. */
export { telemetry } from './telemetry';

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Start logging. Call once, as early in `main.tsx` as possible.
 *
 * Ordering matters and is not arbitrary:
 *
 * 1. **Error reporting first.** It installs the `window.onerror` hooks, so
 *    anything that throws during the rest of startup is captured.
 * 2. **Error instrumentation second**, for the same reason.
 * 3. **Session last**, because it emits `web_session_started` and there is
 *    no value in emitting an event the first two steps might have to
 *    describe the failure of.
 *
 * Safe to call more than once in the sense that it will not throw, but it
 * would register duplicate listeners — so don't.
 */
export function initLogging(): void {
  try {
    initErrorReporting();
    initErrorInstrumentation();
    initSessionInstrumentation();
    diagnostic('initialized', telemetry.describe());
  } catch (error) {
    // Logging failing to start must not stop the app from rendering.
    diagnostic('initialization failed', error);
  }
}
